import React, { useEffect, useState, useMemo, useRef } from 'react';
import { AlertCircle, Pencil } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { dashboardAPI } from '../services/api';
import { bdaOptions } from '../utils/bdeOwnership';

const tooltipStyle = {
  backgroundColor: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: '6px',
  fontSize: '12px',
  boxShadow: '0 2px 4px -1px rgba(0,0,0,0.08)'
};

const StatCard = ({ label, value, suffix, subtitle }) => (
  <div className="rounded border border-gray-200 bg-white px-4 py-3">
    <p className="text-xs font-medium uppercase tracking-wider text-gray-500">{label}</p>
    <p className="text-2xl font-semibold text-gray-900 mt-1">
      {value == null ? '—' : value}
      {value != null && suffix && <span className="text-sm font-medium ml-1 text-gray-500">{suffix}</span>}
    </p>
    {subtitle && <p className="text-xs mt-1 text-gray-500">{subtitle}</p>}
  </div>
);

const formatDays = (n) => (n == null ? '—' : `${n}d`);
// Days from `from` to `to`; returns null if either is missing or unparseable.
// Used in the funnel table to show "days since Stage 1" under each stage.
const daysBetween = (from, to) => {
  if (!from || !to) return null;
  const a = new Date(from);
  const b = new Date(to);
  if (isNaN(a) || isNaN(b)) return null;
  const diff = (b - a) / (1000 * 60 * 60 * 24);
  return Number.isFinite(diff) ? Math.round(diff * 10) / 10 : null;
};
const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  return isNaN(d) ? iso : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/**
 * Click-to-edit date cell. Click to reveal an inline date input; Enter or blur
 * saves, Escape cancels.
 */
function EditableDateCell({ value, onSave, disabled = false, allowClear = true, saving = false, hint }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value || ''); }, [value]);
  useEffect(() => {
    if (editing) {
      setTimeout(() => {
        inputRef.current?.focus();
        try { inputRef.current?.showPicker?.(); } catch { /* showPicker is not universally supported */ }
      }, 0);
    }
  }, [editing]);

  const commit = () => {
    const normalized = (draft || '').trim() || null;
    setEditing(false);
    if (normalized === (value || null)) return;
    if (normalized === null && !allowClear) { setDraft(value || ''); return; }
    onSave(normalized);
  };

  if (disabled) {
    return <span className="text-xs text-gray-500" title={hint || 'Not editable'}>{formatDate(value)}</span>;
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="date"
        value={draft || ''}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          else if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); }
        }}
        className="border border-[#679632] rounded px-1.5 py-0.5 text-xs focus:outline-none"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      disabled={saving}
      className={`group inline-flex items-center gap-1 text-xs rounded px-1.5 py-0.5 transition-colors ${
        value ? 'text-gray-800 hover:bg-gray-100' : 'text-gray-400 hover:bg-gray-100'
      } ${saving ? 'opacity-50 cursor-wait' : ''}`}
      title={hint || 'Click to edit'}
    >
      <span>{formatDate(value)}</span>
      <Pencil size={11} className="opacity-0 group-hover:opacity-60" />
    </button>
  );
}

/** Click-to-edit single-line text cell. */
function EditableTextCell({ value, onSave, saving = false, placeholder }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value || ''); }, [value]);
  useEffect(() => { if (editing) setTimeout(() => inputRef.current?.focus(), 0); }, [editing]);

  const commit = () => {
    const next = (draft || '').trim();
    setEditing(false);
    if (next === (value || '').trim()) return;
    if (!next) { setDraft(value || ''); return; } // don't allow blanking out the company name
    onSave(next);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={draft || ''}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          else if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); }
        }}
        placeholder={placeholder}
        className="border border-[#679632] rounded px-2 py-1 text-sm w-full focus:outline-none"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      disabled={saving}
      className={`group inline-flex items-center gap-1 text-sm rounded px-1.5 py-0.5 transition-colors text-left w-full ${
        value ? 'text-gray-900 hover:bg-gray-100' : 'text-gray-400 hover:bg-gray-100'
      } ${saving ? 'opacity-50 cursor-wait' : ''}`}
      title="Click to edit"
    >
      <span className="font-medium flex-1 truncate">{value || placeholder || '—'}</span>
      <Pencil size={11} className="opacity-0 group-hover:opacity-60 flex-shrink-0" />
    </button>
  );
}

/** Click-to-edit BDE dropdown. Writes bde_name; backend resolves to id. */
function EditableBdeCell({ value, bdeOptions, onSave, saving = false }) {
  const [editing, setEditing] = useState(false);
  const selectRef = useRef(null);

  useEffect(() => { if (editing) setTimeout(() => selectRef.current?.focus(), 0); }, [editing]);

  if (editing) {
    return (
      <select
        ref={selectRef}
        defaultValue={value || ''}
        onBlur={(e) => {
          const next = (e.target.value || '').trim();
          setEditing(false);
          if (next === (value || '').trim()) return;
          onSave(next || null);
        }}
        onChange={(e) => {
          const next = (e.target.value || '').trim();
          setEditing(false);
          if (next === (value || '').trim()) return;
          onSave(next || null);
        }}
        onKeyDown={(e) => { if (e.key === 'Escape') setEditing(false); }}
        className="border border-[#679632] rounded px-2 py-1 text-sm focus:outline-none"
      >
        <option value="">Unassigned</option>
        {bdeOptions.map(name => <option key={name} value={name}>{name}</option>)}
      </select>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      disabled={saving}
      className={`group inline-flex items-center gap-1 text-sm rounded px-1.5 py-0.5 transition-colors text-left w-full ${
        value ? 'text-gray-700 hover:bg-gray-100' : 'text-gray-400 hover:bg-gray-100'
      } ${saving ? 'opacity-50 cursor-wait' : ''}`}
      title="Click to reassign BDE"
    >
      <span className="flex-1 truncate">{value || 'Unassigned'}</span>
      <Pencil size={11} className="opacity-0 group-hover:opacity-60 flex-shrink-0" />
    </button>
  );
}

export default function OnboardingFunnel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bdeFilter, setBdeFilter] = useState('All');
  const [allBdeUsers, setAllBdeUsers] = useState([]);
  const [savingId, setSavingId] = useState(null);

  const loadFunnel = async () => {
    try {
      setLoading(true);
      setError(null);
      const resp = await dashboardAPI.getOnboardingFunnel();
      if (resp?.success) setData(resp.data);
      else setError(resp?.error || 'Failed to load onboarding funnel');
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [funnelResp, bdeResp] = await Promise.all([
          dashboardAPI.getOnboardingFunnel(),
          dashboardAPI.getBDEUsers()
        ]);
        if (cancelled) return;
        if (funnelResp?.success) setData(funnelResp.data);
        else setError(funnelResp?.error || 'Failed to load onboarding funnel');
        if (Array.isArray(bdeResp?.data)) {
          setAllBdeUsers(bdeResp.data.filter(b => b && b.name));
        }
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.error || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Save a single field edit (date, company, or BDE) with optimistic local update + refetch.
  const saveRowEdit = async (client, payload) => {
    setSavingId(client.id);
    // Optimistic local merge so the edit is visible immediately.
    setData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        clients: prev.clients.map(c => c.id === client.id ? { ...c, ...payload } : c)
      };
    });
    try {
      const resp = await dashboardAPI.updateOnboardingFunnelDates(client.id, payload);
      if (!resp?.success) throw new Error(resp?.error || 'Update failed');
      if (resp.warnings && resp.warnings.length) {
        alert(resp.warnings.join('\n'));
      }
      await loadFunnel();
    } catch (e) {
      alert(`Update failed: ${e.response?.data?.error || e.message}`);
      await loadFunnel();
    } finally {
      setSavingId(null);
    }
  };

  const overall = data?.overall;
  const byBDE = data?.byBDE || [];
  const allClients = data?.clients || [];

  // BDE dropdown options for the filter and the row editor come from the canonical
  // bde_users list when available, with a fallback to names observed in the data.
  const bdeOptions = useMemo(() => {
    // Same list as everywhere else: the BDAs, trimmed and sorted, no admins.
    const fromUsers = bdaOptions(allBdeUsers).map(b => b.name);
    if (fromUsers.length) return fromUsers;
    const seen = new Set();
    allClients.forEach(c => { if (c.bde_name) seen.add(c.bde_name); });
    return Array.from(seen).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [allBdeUsers, allClients]);

  const bdeFilterOptions = useMemo(() => ['All', ...bdeOptions], [bdeOptions]);

  const clients = useMemo(() => {
    if (bdeFilter === 'All') return allClients;
    return allClients.filter(c => (c.bde_name || '') === bdeFilter);
  }, [allClients, bdeFilter]);

  const filteredOverall = useMemo(() => {
    if (bdeFilter === 'All') return overall;
    if (!clients.length) return { totalOnboarded: 0 };
    const stats = (vals) => {
      const nums = vals.filter(v => typeof v === 'number' && v >= 0);
      if (!nums.length) return { avg: null, median: null, min: null, max: null, count: 0 };
      const sorted = [...nums].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      return {
        avg: Math.round((nums.reduce((s, v) => s + v, 0) / nums.length) * 10) / 10,
        median: Math.round((sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid]) * 10) / 10,
        min: sorted[0],
        max: sorted[sorted.length - 1],
        count: nums.length
      };
    };
    return {
      totalOnboarded: clients.length,
      identifiedToFirstFollowup: stats(clients.map(c => c.days_identified_to_first_followup)),
      firstFollowupToFirstMeeting: stats(clients.map(c => c.days_first_followup_to_first_meeting)),
      firstMeetingToOnboarded: stats(clients.map(c => c.days_first_meeting_to_onboarded)),
      identifiedToOnboarded: stats(clients.map(c => c.days_identified_to_onboarded))
    };
  }, [bdeFilter, overall, clients]);

  const bdeChartData = useMemo(() => (
    byBDE.map(b => ({
      name: b.name,
      'Identify to First Follow-up': b.identifiedToFirstFollowup ?? 0,
      'First Follow-up to Meeting': b.firstFollowupToFirstMeeting ?? 0,
      'Meeting to Onboarded': b.firstMeetingToOnboarded ?? 0
    }))
  ), [byBDE]);

  if (loading) {
    return (
      <div className="text-center py-16">
        <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-[#679632]" />
        <p className="mt-4 text-gray-500 text-sm">Loading onboarding funnel...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-gray-200">
        <AlertCircle size={32} className="mx-auto text-gray-400 mb-3" />
        <p className="text-red-600 text-sm">{error}</p>
      </div>
    );
  }

  if (!filteredOverall || !filteredOverall.totalOnboarded) {
    return (
      <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-gray-200">
        <AlertCircle size={32} className="mx-auto text-gray-400 mb-3" />
        <p className="text-gray-500 text-sm">No onboarded clients found for this selection.</p>
      </div>
    );
  }

  const kpi = filteredOverall;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Onboarding Funnel</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Average time through each stage: Identify, First Follow-up, Meeting, Onboarded.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">BDE</span>
          <select
            value={bdeFilter}
            onChange={(e) => setBdeFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
          >
            {bdeFilterOptions.map(name => <option key={name} value={name}>{name}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          label="Identify to First Follow-up"
          value={kpi.identifiedToFirstFollowup?.avg}
          suffix="days avg"
          subtitle={`Median ${formatDays(kpi.identifiedToFirstFollowup?.median)} · n=${kpi.identifiedToFirstFollowup?.count || 0}`}
        />
        <StatCard
          label="First Follow-up to Meeting"
          value={kpi.firstFollowupToFirstMeeting?.avg}
          suffix="days avg"
          subtitle={`Median ${formatDays(kpi.firstFollowupToFirstMeeting?.median)} · n=${kpi.firstFollowupToFirstMeeting?.count || 0}`}
        />
        <StatCard
          label="Meeting to Onboarded"
          value={kpi.firstMeetingToOnboarded?.avg}
          suffix="days avg"
          subtitle={`Median ${formatDays(kpi.firstMeetingToOnboarded?.median)} · n=${kpi.firstMeetingToOnboarded?.count || 0}`}
        />
        <StatCard
          label="Identify to Onboarded"
          value={kpi.identifiedToOnboarded?.avg}
          suffix="days avg"
          subtitle={`Median ${formatDays(kpi.identifiedToOnboarded?.median)} · n=${kpi.identifiedToOnboarded?.count || 0}`}
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Onboarded (selected)" value={kpi.totalOnboarded} />
        <StatCard label="Fastest total" value={formatDays(kpi.identifiedToOnboarded?.min)} />
        <StatCard label="Slowest total" value={formatDays(kpi.identifiedToOnboarded?.max)} />
        <StatCard label="Median total" value={formatDays(kpi.identifiedToOnboarded?.median)} />
      </div>

      {byBDE.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="mb-3">
            <h4 className="text-sm font-semibold text-gray-800">Average stage time, by BDE</h4>
            <p className="text-xs text-gray-500 mt-0.5">Each bar is the average days spent in that stage for clients onboarded by that BDE.</p>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={bdeChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="name" stroke="#9ca3af" style={{ fontSize: '11px' }} />
              <YAxis stroke="#9ca3af" style={{ fontSize: '11px' }} label={{ value: 'days', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#9ca3af' } }} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v}d`, '']} />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Bar dataKey="Identify to First Follow-up" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              <Bar dataKey="First Follow-up to Meeting" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Meeting to Onboarded" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <h4 className="text-sm font-semibold text-gray-800">Onboarded clients ({clients.length}) — 8-stage funnel</h4>
          <p className="text-xs text-gray-500 mt-0.5">
            Each row tracks the eight onboarding stages. Click any date cell to edit. Edits sync back to the
            lead, follow-up, post-sales, and onboarded-client records. The <span className="text-gray-700">small "+Xd"</span> under each
            date shows days elapsed from <span className="text-gray-700">Stage 1 (Lead Created)</span>, so you can see how far into the funnel each stage landed.
            The rightmost column is the total days from stage 1 to stage 8.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">Company</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">BDE</th>
                <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">1. Lead Created</th>
                <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">2. Follow-ups</th>
                <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">3. First Response</th>
                <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">4. First Meeting</th>
                <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">5. Secondary Meeting</th>
                <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">6. Proposal</th>
                <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">7. Contract</th>
                <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">8. Onboarded</th>
                <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">Total days</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {clients.map(c => {
                const isSaving = savingId === c.id;
                // Render a date cell with a subscript showing days from Stage 1
                // (Lead Created) to this stage's date — i.e. "+Xd into the funnel".
                // `cumulativeFromStart` overrides the auto-computation when needed
                // (e.g. consecutive deltas are no longer used here).
                const dateWithCumulative = (cellValue, onSave, opts = {}) => {
                  const delta = daysBetween(c.identified_date, cellValue);
                  return (
                    <div className="flex flex-col items-center">
                      <EditableDateCell value={cellValue} saving={isSaving} onSave={onSave} {...opts} />
                      {delta != null && (
                        <span
                          className="text-[10px] text-gray-400 mt-0.5"
                          title={`Days from Stage 1 (Lead Created) to this stage`}
                        >
                          +{formatDays(delta)}
                        </span>
                      )}
                    </div>
                  );
                };
                return (
                  <tr key={c.id} className={`hover:bg-gray-50 ${isSaving ? 'opacity-80' : ''}`}>
                    <td className="px-3 py-2 min-w-[160px]">
                      <EditableTextCell
                        value={c.company_name}
                        saving={isSaving}
                        onSave={(v) => saveRowEdit(c, { company_name: v })}
                      />
                    </td>
                    <td className="px-3 py-2 min-w-[130px]">
                      <EditableBdeCell
                        value={c.bde_name}
                        bdeOptions={bdeOptions}
                        saving={isSaving}
                        onSave={(v) => saveRowEdit(c, { bde_name: v })}
                      />
                    </td>
                    {/* 1. Lead Created — anchor stage, no "+Xd" since +0d is implied */}
                    <td className="px-3 py-2 text-xs text-center">
                      <EditableDateCell
                        value={c.identified_date}
                        saving={isSaving}
                        onSave={(v) => saveRowEdit(c, { identified_date: v })}
                      />
                    </td>
                    {/* 2. Total follow-ups (count, not a date) */}
                    <td className="px-3 py-2 text-center">
                      <span
                        className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs font-semibold"
                        title="Total follow-ups logged for this lead"
                      >
                        {c.total_follow_ups ?? 0}
                      </span>
                    </td>
                    {/* 3. First Response */}
                    <td className="px-3 py-2 text-xs text-center">
                      {dateWithCumulative(
                        c.first_response_date,
                        (v) => saveRowEdit(c, { first_response_date: v })
                      )}
                    </td>
                    {/* 4. First Meeting */}
                    <td className="px-3 py-2 text-xs text-center">
                      {dateWithCumulative(
                        c.first_meeting_date,
                        (v) => saveRowEdit(c, { first_meeting_date: v })
                      )}
                    </td>
                    {/* 5. Secondary Meeting (optional) */}
                    <td className="px-3 py-2 text-xs text-center">
                      {dateWithCumulative(
                        c.secondary_meeting_date,
                        (v) => saveRowEdit(c, { secondary_meeting_date: v })
                      )}
                    </td>
                    {/* 6. Proposal introduced */}
                    <td className="px-3 py-2 text-xs text-center">
                      {dateWithCumulative(
                        c.proposal_date,
                        (v) => saveRowEdit(c, { proposal_date: v })
                      )}
                    </td>
                    {/* 7. Contract finalised */}
                    <td className="px-3 py-2 text-xs text-center">
                      {dateWithCumulative(
                        c.contract_finalized_date,
                        (v) => saveRowEdit(c, { contract_finalized_date: v })
                      )}
                    </td>
                    {/* 8. Onboarded (required) */}
                    <td className="px-3 py-2 text-xs text-center">
                      {dateWithCumulative(
                        c.onboarded_date,
                        (v) => saveRowEdit(c, { onboarded_date: v }),
                        { allowClear: false, hint: 'Onboarding date cannot be blank.' }
                      )}
                    </td>
                    {/* Total days 1 → 8 */}
                    <td className="px-3 py-2 text-sm text-center font-semibold text-gray-900">
                      {formatDays(c.days_identified_to_onboarded)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
